import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { accountsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Upload, FileText, CheckCircle2, XCircle, AlertTriangle, 
  HelpCircle, Download, Loader2, Eye, RefreshCw
} from 'lucide-react';
import PeriodToolbar, { FilingActionsNotice } from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES, formatMonth } from '../lib/period';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const styles = {
    MATCHED: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    MISMATCH: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    MISSING: 'bg-red-500/10 text-red-400 border-red-500/20',
    EXTRA: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };
  
  const icons = {
    MATCHED: <CheckCircle2 className="w-3 h-3 mr-1" />,
    MISMATCH: <AlertTriangle className="w-3 h-3 mr-1" />,
    MISSING: <XCircle className="w-3 h-3 mr-1" />,
    EXTRA: <HelpCircle className="w-3 h-3 mr-1" />,
  };

  return (
    <Badge variant="outline" className={styles[status] || ''}>
      {icons[status]}
      {status}
    </Badge>
  );
};

const Gstr2bMonthTable = ({ data, onPickMonth }) => (
  <Card className="bg-card/50 backdrop-blur-sm border-border/50" data-testid="gstr2b-month-table">
    <CardHeader><CardTitle>GSTR-2B by filing month</CardTitle></CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            <TableHead>2B upload</TableHead>
            <TableHead className="text-right">Invoices</TableHead>
            <TableHead>Reconciliation</TableHead>
            <TableHead className="text-right">Exceptions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data?.monthly || []).map((r) => {
            const sm = r.summary || {};
            const exceptions = (sm.mismatch || 0) + (sm.missing || 0) + (sm.extra || 0);
            return (
              <TableRow key={r.tax_period} className="cursor-pointer hover:bg-muted/30" onClick={() => onPickMonth(r.tax_period)} data-testid={`gstr2b-month-row-${r.tax_period}`}>
                <TableCell className="font-medium">{formatMonth(r.tax_period)}</TableCell>
                <TableCell>{r.uploaded ? <Badge variant="outline" className="text-emerald-400">Uploaded</Badge> : <Badge variant="outline" className="text-zinc-400">Pending</Badge>}</TableCell>
                <TableCell className="text-right font-mono">{r.invoiceCount || 0}</TableCell>
                <TableCell>{r.reconciled ? <Badge variant="outline" className="text-emerald-400">Reconciled</Badge> : <Badge variant="outline" className="text-amber-400">Open</Badge>}</TableCell>
                <TableCell className="text-right font-mono">{r.reconciled ? exceptions : '—'}</TableCell>
              </TableRow>
            );
          })}
          <TableRow className="border-t-2 border-border/80 font-semibold" data-testid="gstr2b-total-row">
            <TableCell>Total Selected Period</TableCell>
            <TableCell />
            <TableCell className="text-right font-mono">{data?.totals?.invoiceCount || 0}</TableCell>
            <TableCell colSpan={2} className="text-xs text-muted-foreground">Click a month to upload or reconcile it</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const Gstr2bPage = () => {
  const { selectedOrg } = useApp();
  const periodState = usePeriod(PERIOD_MODES.MONTH_SELECTION);
  const months = periodState.months;
  const monthsKey = months.join(',');
  const multi = months.length > 1;
  // Upload / reconcile / exceptions are filing-month actions → only with exactly one month.
  const currentPeriod = multi ? null : months[0];
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('ALL');

  useEffect(() => {
    if (selectedOrg) {
      loadReconciliationStatus();
    }
  }, [selectedOrg, monthsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReconciliationStatus = async () => {
    setLoading(true);
    setReconciliationData(null);
    try {
      const response = await accountsAPI.getGstr2bStatus(months);
      if (response.data) {
        setReconciliationData(response.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadedFile(file);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tax_period', currentPeriod);

      const response = await accountsAPI.uploadGstr2b(formData);
      if (response.data) {
        setReconciliationData(prev => ({
          ...prev,
          uploaded: true,
          uploadedAt: new Date().toISOString(),
          fileName: file.name,
          parsed: response.data.parsed || false,
          invoiceCount: response.data.invoice_count || 0
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      alert('Failed to upload file. Please ensure it is a valid JSON or XLSX format.');
    }
    setUploading(false);
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const response = await accountsAPI.reconcileGstr2b(currentPeriod);
      if (response.data) {
        setReconciliationData(response.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      alert('Failed to reconcile. Please try again.');
    }
    setReconciling(false);
  };

  const handleExportExceptions = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/accounts/exports/xlsx/gstr2b_exceptions?tax_period=${currentPeriod}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gstr2b_exceptions_${currentPeriod}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const getFilteredEntries = () => {
    if (!reconciliationData?.entries) return [];
    if (activeTab === 'ALL') return reconciliationData.entries;
    return reconciliationData.entries.filter(e => e.status === activeTab);
  };

  const getStatusCounts = () => {
    if (!reconciliationData?.entries) return { ALL: 0, MATCHED: 0, MISMATCH: 0, MISSING: 0, EXTRA: 0 };
    const entries = reconciliationData.entries;
    return {
      ALL: entries.length,
      MATCHED: entries.filter(e => e.status === 'MATCHED').length,
      MISMATCH: entries.filter(e => e.status === 'MISMATCH').length,
      MISSING: entries.filter(e => e.status === 'MISSING').length,
      EXTRA: entries.filter(e => e.status === 'EXTRA').length,
    };
  };

  const counts = getStatusCounts();
  const filteredEntries = getFilteredEntries();

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gstr2b-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">GSTR-2B Reconciliation</h1>
          <p className="text-muted-foreground mt-1">
            Upload and reconcile GSTR-2B with your purchase register
          </p>
        </div>
        <div className="flex items-center gap-4">
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.MONTH_SELECTION} resolved={reconciliationData} />
        </div>
      </div>

      {multi ? (
        <>
          <FilingActionsNotice months={months} />
          <Gstr2bMonthTable data={reconciliationData} onPickMonth={(m) => periodState.setMonths([m])} />
        </>
      ) : (<>
      {/* Upload Section */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload GSTR-2B
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1">
              <Input
                type="file"
                accept=".json,.xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                className="bg-background/50"
                data-testid="gstr2b-file-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Supported formats: JSON, XLSX (GST Portal export)
              </p>
            </div>
            {reconciliationData?.uploaded && (
              <div className="flex flex-col gap-1">
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  File Uploaded
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {reconciliationData.fileName || 'GSTR-2B'} • {reconciliationData.invoiceCount || 0} invoices
                </span>
              </div>
            )}
            <Button
              onClick={handleReconcile}
              disabled={!reconciliationData?.uploaded || reconciling}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="reconcile-btn"
            >
              {reconciling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Reconcile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status Cards */}
      {reconciliationData?.entries && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{counts.ALL}</p>
              <p className="text-xs text-muted-foreground">Total Entries</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/5 border-emerald-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">{counts.MATCHED}</p>
              <p className="text-xs text-emerald-400/70">Matched</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-500/5 border-amber-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{counts.MISMATCH}</p>
              <p className="text-xs text-amber-400/70">Mismatch</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/5 border-red-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-400">{counts.MISSING}</p>
              <p className="text-xs text-red-400/70">Missing in Books</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/5 border-purple-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-400">{counts.EXTRA}</p>
              <p className="text-xs text-purple-400/70">Extra in Books</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reconciliation Results */}
      {reconciliationData?.entries && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Reconciliation Results
            </CardTitle>
            <Button variant="outline" size="sm" onClick={handleExportExceptions} data-testid="export-exceptions-btn">
              <Download className="w-4 h-4 mr-2" />
              Export Exceptions
            </Button>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="ALL" data-testid="tab-all">
                  All ({counts.ALL})
                </TabsTrigger>
                <TabsTrigger value="MISMATCH" data-testid="tab-mismatch">
                  Mismatch ({counts.MISMATCH})
                </TabsTrigger>
                <TabsTrigger value="MISSING" data-testid="tab-missing">
                  Missing ({counts.MISSING})
                </TabsTrigger>
                <TabsTrigger value="EXTRA" data-testid="tab-extra">
                  Extra ({counts.EXTRA})
                </TabsTrigger>
                <TabsTrigger value="MATCHED" data-testid="tab-matched">
                  Matched ({counts.MATCHED})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab}>
                <div className="rounded-lg border border-border/50 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Invoice No</TableHead>
                        <TableHead>Vendor GSTIN</TableHead>
                        <TableHead>Invoice Date</TableHead>
                        <TableHead className="text-right">Taxable Value</TableHead>
                        <TableHead className="text-right">IGST</TableHead>
                        <TableHead className="text-right">CGST</TableHead>
                        <TableHead className="text-right">SGST</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEntries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                            No entries found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredEntries.map((entry, idx) => (
                          <TableRow 
                            key={entry.invoice_no || idx}
                            className="cursor-pointer hover:bg-muted/20"
                            onClick={() => setSelectedEntry(entry)}
                            data-testid={`gstr2b-row-${idx}`}
                          >
                            <TableCell className="font-mono">{entry.invoice_no || '-'}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.vendor_gstin || '-'}</TableCell>
                            <TableCell>{entry.invoice_date || '-'}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(entry.taxable_value)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(entry.igst)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(entry.cgst)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(entry.sgst)}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={entry.status} />
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Entry Detail Dialog */}
      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-4xl bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Invoice Comparison
              {selectedEntry && <StatusBadge status={selectedEntry.status} />}
            </DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="grid grid-cols-2 gap-6">
              {/* GSTR-2B Data */}
              <div className="space-y-4">
                <h3 className="font-medium text-emerald-400 border-b border-emerald-500/20 pb-2">
                  GSTR-2B (Government Portal)
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Invoice No</span>
                    <span className="font-mono">{selectedEntry.invoice_no || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GSTIN</span>
                    <span className="font-mono">{selectedEntry.vendor_gstin || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    <span>{selectedEntry.invoice_date || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Taxable Value</span>
                    <span className="font-mono">{formatCurrency(selectedEntry.taxable_value)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">IGST</span>
                    <span className="font-mono">{formatCurrency(selectedEntry.igst)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CGST</span>
                    <span className="font-mono">{formatCurrency(selectedEntry.cgst)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">SGST</span>
                    <span className="font-mono">{formatCurrency(selectedEntry.sgst)}</span>
                  </div>
                </div>
              </div>

              {/* Books Data */}
              <div className="space-y-4">
                <h3 className="font-medium text-blue-400 border-b border-blue-500/20 pb-2">
                  Books (Purchase Register)
                </h3>
                {selectedEntry.matched_bill ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Bill No</span>
                      <span className="font-mono">{selectedEntry.matched_bill.bill_number || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Vendor</span>
                      <span>{selectedEntry.matched_bill.vendor_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date</span>
                      <span>{selectedEntry.matched_bill.bill_date || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Taxable Value</span>
                      <span className={`font-mono ${
                        selectedEntry.taxable_value !== selectedEntry.matched_bill.taxable_amount 
                          ? 'text-amber-400' : ''
                      }`}>
                        {formatCurrency(selectedEntry.matched_bill.taxable_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">GST Amount</span>
                      <span className="font-mono">{formatCurrency(selectedEntry.matched_bill.gst_amount)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">
                    <p>No matching bill found in books</p>
                  </div>
                )}
              </div>

              {/* Differences */}
              {selectedEntry.differences && selectedEntry.differences.length > 0 && (
                <div className="col-span-2 mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <h4 className="font-medium text-amber-400 mb-2">Differences Found</h4>
                  <ul className="space-y-1 text-sm">
                    {selectedEntry.differences.map((diff, i) => (
                      <li key={`diff-${i}`} className="flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3 text-amber-400" />
                        <span>{diff}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
};

export default Gstr2bPage;
